// reports.view — Laporan Servis (basis kas / tgl ambil). Angka dari domain/money.
import { opsiCabangHTML } from '@core/cabangOpsi';
import { rp, esc } from '@domain/format';
import { currentUser } from '@core/session';
import { canSeeModal } from '@domain/access';
import { omzetTiket, modalTiket, profitTiket, piutangTiket } from '@domain/money';
import { downloadFile, csvCell } from '@ui/download';
import { downloadXLS } from '@ui/xls';
import { searchBoxHTML, wireSearchBox } from '@ui/searchbox';
import type { Role } from '@core/types';
import * as repo from './reports.repo';

const MN = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
let repBulan = 0, repTahun = 0, repTeknisi = '', repCabang = '';
let repTgl = ''; // isi (yyyy-mm-dd) = mode HARIAN: hanya tanggal itu; kosong = bulanan (default)
let repQ = ''; // cari no servis / pelanggan / unit — laporan bisa ratusan baris per periode

const inPeriod = (iso: string | null, y: number, m: number): boolean => {
  if (!iso) return false;
  const d = new Date(iso);
  return d.getFullYear() === y && d.getMonth() + 1 === m;
};
// Bandingkan pada tanggal LOKAL (bukan potong UTC): tgl_ambil disimpan mis. "…T17:00:00Z" =
// keesokan harinya di WIB. Harus konsisten dengan inPeriod (yang juga pakai waktu lokal) & input date.
const sameDay = (iso: string | null, tgl: string): boolean => {
  if (!iso) return false;
  const d = new Date(iso);
  const lokal = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return lokal === tgl;
};

export async function renderLapServis(root: HTMLElement): Promise<void> {
  const u = currentUser()!;
  const now = new Date();
  if (!repBulan) repBulan = now.getMonth() + 1;
  if (!repTahun) repTahun = now.getFullYear();
  const seeModal = canSeeModal(u.role);

  root.innerHTML = `<div class="ttl">📊 Laporan Servis</div><div class="panel"><div class="empty">Memuat…</div></div>`;
  let items: Awaited<ReturnType<typeof repo.fetchAll>>;
  try { items = await repo.fetchAll(); }
  catch (e) { root.innerHTML = `<div class="ttl">📊 Laporan Servis</div><div class="panel err">${esc((e as Error).message)}</div>`; return; }

  // TANPA cadangan ke tanggal terima: `omzetTiket` menghitung DP sebagai kas diakui, jadi unit
  // yang masih di meja teknisi ikut masuk kolom Omzet — dan laporan mengaku "profit" atas
  // pekerjaan yang belum tentu berhasil. Aturan terkunci: metrik uang per tanggal AMBIL.
  const periodSet = items.filter((i) => { const iso = i.tglAmbilISO;
    return repTgl ? sameDay(iso, repTgl) : inPeriod(iso, repTahun, repBulan); });
  const isMgr = u.role === 'Owner' || u.role === 'Manager';
  const teknisiList = [...new Set(periodSet.map((i) => i.ticket.teknisi).filter((t): t is string => !!t))].sort();
  // Dari DAFTAR cabang, bukan dari data periode ini: cabang yang baru dibuka (atau yang bulan
  // ini kebetulan nihil transaksi) dulu hilang dari filter, sehingga tak bisa dipilih sama sekali.
  const opsiCab = await opsiCabangHTML(repCabang);
  if (repTeknisi && !teknisiList.includes(repTeknisi)) repTeknisi = ''; // clamp stale (pelajaran doubt-driven Menu 10)
  if (u.role === 'Teknisi') repTeknisi = ''; // teknisi sudah RLS-scoped ke diri
  const rq = repQ.trim().toLowerCase();
  const per = periodSet.filter((i) => (!repTeknisi || i.ticket.teknisi === repTeknisi) && (!repCabang || i.ticket.outlet === repCabang))
    .filter((i) => !rq || `${i.ticket.no} ${i.custNama} ${i.unit} ${i.ticket.teknisi ?? ''}`.toLowerCase().includes(rq));
  const perTanpaCari = periodSet.filter((i) => (!repTeknisi || i.ticket.teknisi === repTeknisi) && (!repCabang || i.ticket.outlet === repCabang)).length;
  const jadi = per.filter((i) => i.ticket.hasil === 'jadi').length;
  const gagal = per.filter((i) => i.ticket.hasil === 'tidakbisa').length;
  const omzet = per.reduce((a, i) => a + omzetTiket(i.ticket), 0);
  const modal = per.reduce((a, i) => a + modalTiket(i.ticket), 0);
  const profit = per.reduce((a, i) => a + profitTiket(i.ticket), 0);
  const piutang = per.reduce((a, i) => a + piutangTiket(i.ticket), 0);

  const card = (l: string, v: string, s = ''): string => `<div class="stat"><div class="lbl">${esc(l)}</div><div class="num" style="${s}">${esc(v)}</div></div>`;
  const selStyle = 'height:38px;border:1px solid var(--line);border-radius:8px;padding:0 10px;background:var(--surface);color:var(--txt)';
  let years = '';
  for (let y = 2024; y <= now.getFullYear() + 1; y++) years += `<option value="${y}" ${repTahun === y ? 'selected' : ''}>${y}</option>`;

  const HASIL_LBL: Record<string, string> = { jadi: 'Jadi', tidakbisa: 'Tidak bisa', batal: 'Batal' };
  const hasilLbl = (t: repo.RepItem['ticket']): string => HASIL_LBL[t.hasil ?? ''] ?? (t.status ?? '—');
  const rowsHtml = per.map((i) => `<tr><td><b>${esc(i.ticket.no)}</b></td><td>${esc(i.custNama)}</td><td>${esc(i.unit)}</td><td>${esc(i.ticket.teknisi ?? '—')}</td><td>${esc(i.tindakan)}</td><td>${esc(hasilLbl(i.ticket))}</td><td class="r">${rp(omzetTiket(i.ticket))}</td>${seeModal ? `<td class="r">${rp(modalTiket(i.ticket))}</td><td class="r">${rp(profitTiket(i.ticket))}</td>` : ''}</tr>`).join('')
    || `<tr><td colspan="${seeModal ? 9 : 7}" class="empty">Tidak ada data periode ini.</td></tr>`;

  root.innerHTML = `<div class="ttl">📊 Laporan Servis</div>
    <div class="scope-note">Basis kas (tgl ambil) · periode <b>${repTgl ? 'Harian ' + repTgl : `${MN[repBulan]} ${repTahun}`}</b> · ${seeModal ? '' : 'modal/profit hanya Owner/Manager/SPV/Finance'}</div>
    <div class="toolbar" style="display:flex;gap:8px;margin-bottom:12px;flex-wrap:wrap">
      <select id="r_bln" style="${selStyle}" ${repTgl ? 'disabled' : ''}>${MN.map((mm, idx) => idx ? `<option value="${idx}" ${repBulan === idx ? 'selected' : ''}>${mm}</option>` : '').join('')}</select>
      <select id="r_thn" style="${selStyle}" ${repTgl ? 'disabled' : ''}>${years}</select>
      <input type="date" id="r_tgl" style="${selStyle}" value="${esc(repTgl)}" title="Cek harian — pilih 1 tanggal (kosongkan untuk kembali ke bulanan)">
      ${repTgl ? `<button class="btn" id="r_tgl_clr" style="${selStyle}" title="Kembali ke laporan bulanan">✕ Harian</button>` : ''}
      ${u.role !== 'Teknisi' ? `<select id="r_tek" style="${selStyle}"><option value="">Semua teknisi</option>${teknisiList.map((t) => `<option value="${esc(t)}" ${repTeknisi === t ? 'selected' : ''}>${esc(t)}</option>`).join('')}</select>` : ''}
      ${isMgr ? `<select id="r_cab" style="${selStyle}">${opsiCab}</select>` : ''}
      <button class="btn" id="r_reset" style="${selStyle}" title="Bersihkan filter" aria-label="Bersihkan filter">↺</button>
      ${searchBoxHTML('r_cari', repQ, '🔎 Cari no / pelanggan / unit…', 'Cari servisan di laporan')}
      <span style="font-size:12px;color:var(--muted)">Tampil <b>${per.length}</b>${rq ? ` dari ${perTanpaCari}` : ''}</span>
      <button class="btn" id="r_csv">⬇️ CSV</button>
      <button class="btn" id="r_xls">⬇️ Excel</button>
    </div>
    <div class="stat-grid" style="margin-bottom:14px">
      ${card('Selesai', String(jadi))}${card('Gagal', String(gagal))}
      ${card('Omzet', rp(omzet), 'color:var(--ok)')}
      ${seeModal ? card('Modal', rp(modal)) : ''}
      ${seeModal ? card('Profit', rp(profit), profit >= 0 ? 'color:var(--ok)' : 'color:var(--danger)') : ''}
      ${card('Piutang', rp(piutang), 'color:var(--danger)')}
    </div>
    <table class="data"><thead><tr><th>No. Servis</th><th>Pelanggan</th><th>Unit</th><th>Teknisi</th><th>Tindakan</th><th>Hasil</th><th>Omzet</th>${seeModal ? '<th>Modal</th><th>Profit</th>' : ''}</tr></thead><tbody>${rowsHtml || `<tr><td colspan="${seeModal ? 9 : 7}" class="empty">${rq ? 'Tak ada servisan cocok dengan pencarian ini.' : 'Belum ada servisan pada periode ini.'}</td></tr>`}</tbody></table>`;

  const rerender = (): void => { void renderLapServis(root); };
  const blnSel = root.querySelector('#r_bln') as HTMLSelectElement;
  const thnSel = root.querySelector('#r_thn') as HTMLSelectElement;
  blnSel.onchange = () => { repBulan = +blnSel.value; rerender(); };
  thnSel.onchange = () => { repTahun = +thnSel.value; rerender(); };
  const tglInp = root.querySelector('#r_tgl') as HTMLInputElement | null;
  if (tglInp) tglInp.onchange = () => { repTgl = tglInp.value; rerender(); };
  root.querySelector('#r_tgl_clr')?.addEventListener('click', () => { repTgl = ''; rerender(); });
  const tekSel = root.querySelector('#r_tek') as HTMLSelectElement | null;
  if (tekSel) tekSel.onchange = () => { repTeknisi = tekSel.value; rerender(); };
  const cabSel = root.querySelector('#r_cab') as HTMLSelectElement | null;
  if (cabSel) cabSel.onchange = () => { repCabang = cabSel.value; rerender(); };
  root.querySelector('#r_reset')?.addEventListener('click', () => { repTeknisi = ''; repCabang = ''; repQ = ''; repTgl = ''; rerender(); });
  wireSearchBox(root, 'r_cari', (v) => { repQ = v; }, rerender);
  const label = `${repTgl ? repTgl : `${MN[repBulan]}-${repTahun}`}${repTeknisi ? '-' + repTeknisi : ''}${repCabang ? '-' + repCabang : ''}`;
  (root.querySelector('#r_csv') as HTMLElement).addEventListener('click', () => exportCSV(per, u.role, label));
  (root.querySelector('#r_xls') as HTMLElement).addEventListener('click', () => exportXLS(per, u.role, label, { jadi, gagal, omzet, modal, profit, piutang }));
}

function exportXLS(per: repo.RepItem[], role: Role, label: string, sum: { jadi: number; gagal: number; omzet: number; modal: number; profit: number; piutang: number }): void {
  const seeModal = canSeeModal(role);
  const th = ['No. Servis', 'Pelanggan', 'Unit', 'Teknisi', 'Tindakan', 'Hasil', 'Omzet', ...(seeModal ? ['Modal', 'Profit'] : []), 'Piutang'];
  const HASIL_LBL: Record<string, string> = { jadi: 'Jadi', tidakbisa: 'Tidak bisa', batal: 'Batal' };
  const body = per.map((i) => {
    const cells = [esc(i.ticket.no), esc(i.custNama), esc(i.unit), esc(i.ticket.teknisi ?? '—'), esc(i.tindakan), esc(HASIL_LBL[i.ticket.hasil ?? ''] ?? (i.ticket.status ?? '—')), `${omzetTiket(i.ticket)}`, ...(seeModal ? [`${modalTiket(i.ticket)}`, `${profitTiket(i.ticket)}`] : []), `${piutangTiket(i.ticket)}`];
    return `<tr>${cells.map((c, idx) => `<td${idx >= 6 ? ' class="r"' : ''}>${c}</td>`).join('')}</tr>`;
  }).join('');
  const meta = `<h2>Laporan Servis — iFiCs</h2><p>Periode ${esc(label)} · Selesai ${sum.jadi} · Gagal ${sum.gagal} · Omzet ${rp(sum.omzet)}${seeModal ? ` · Modal ${rp(sum.modal)} · Profit ${rp(sum.profit)}` : ''} · Piutang ${rp(sum.piutang)}</p>`;
  downloadXLS(`laporan-servis-${label}.xls`, `${meta}<table><tr>${th.map((h) => `<th>${h}</th>`).join('')}</tr>${body}</table>`);
}

function exportCSV(per: repo.RepItem[], role: Role, label: string): void {
  const seeModal = canSeeModal(role);
  const head = ['No. Servis', 'Pelanggan', 'Unit', 'Tindakan', 'Omzet', ...(seeModal ? ['Modal', 'Profit'] : []), 'Piutang'];
  const lines = [head.map(csvCell).join(',')];
  for (const i of per) {
    const cells: (string | number)[] = [i.ticket.no, i.custNama, i.unit, i.tindakan, omzetTiket(i.ticket)];
    if (seeModal) cells.push(modalTiket(i.ticket), profitTiket(i.ticket));
    cells.push(piutangTiket(i.ticket));
    lines.push(cells.map(csvCell).join(','));
  }
  downloadFile(lines.join('\n'), `laporan-servis-${label}.csv`);
}
